import { FunctionsHttpError } from '@supabase/supabase-js'
import type { No } from '../components/DiagnosticoCusto'
import { supabase } from '../lib/supabase'

export type Painel = 'trafego_pago' | 'organico' | 'redes_sociais'
export type Achado = { tipo: 'resumo' | 'destaque' | 'alerta' | 'oportunidade'; texto: string }

/** O mesmo formato que a função resumo-semanal grava (supabase/functions/resumo-semanal/resumo.ts). */
export type TipoResumo = 'semana' | 'mes'

/** Um número do relatório, já formatado pelo motor, contra o período anterior. */
export type Indicador = {
  grupo: string
  nome: string
  valor: string
  anterior: string | null
  variacao: string | null
  sentido: 'bom' | 'ruim' | null
  meta: string | null
}

export type ConteudoResumo = {
  resumo: string
  destaques: string[]
  atencao: string[]
  acoes: string[]
  paineis: Array<{ painel: Painel; achados: Achado[] }>
  /** Ausentes nos resumos gerados antes do relatório mensal (01/10/2026). */
  indicadores?: Indicador[]
  diagnostico?: No[]
  descartadas: number
  demo: boolean
}

export type ResumoSemanal = {
  id: string
  marcaId: string
  marca: string
  tipo: TipoResumo
  inicio: string
  fim: string
  conteudo: ConteudoResumo
  fonte: 'motor' | 'ia'
  modelo: string | null
  origem: 'manual' | 'agendada'
  geradoEm: string
}

const COLUNAS = 'id, marca_id, tipo, inicio, fim, conteudo, fonte, modelo, origem, gerado_em, marcas(nome)'
type Linha = {
  id: string
  marca_id: string
  tipo: TipoResumo
  inicio: string
  fim: string
  conteudo: unknown
  fonte: 'motor' | 'ia'
  modelo: string | null
  origem: 'manual' | 'agendada'
  gerado_em: string
  marcas: unknown
}
const deLinha = (r: Linha): ResumoSemanal => ({
    id: r.id,
    marcaId: r.marca_id,
    marca: (r.marcas as { nome: string } | null)?.nome ?? '',
    tipo: r.tipo,
    inicio: r.inicio,
    fim: r.fim,
    conteudo: r.conteudo as ConteudoResumo,
    fonte: r.fonte,
    modelo: r.modelo,
    origem: r.origem,
    geradoEm: r.gerado_em,
  })

/** Os resumos das marcas visíveis (o RLS recorta), do mais recente para o mais antigo. */
export async function listarResumos(marcaId: string | null, tipo: TipoResumo): Promise<ResumoSemanal[]> {
  let q = supabase
    .from('resumos')
    .select(COLUNAS)
    .eq('tipo', tipo)
    .order('inicio', { ascending: false })
    .limit(marcaId ? 26 : 60)
  if (marcaId) q = q.eq('marca_id', marcaId)
  const { data, error } = await q
  if (error) throw new Error(error.message)
  return ((data ?? []) as Linha[]).map(deLinha)
}

export async function lerResumo(id: string): Promise<ResumoSemanal | null> {
  const { data, error } = await supabase.from('resumos').select(COLUNAS).eq('id', id).maybeSingle()
  if (error) throw new Error(error.message)
  return data ? deLinha(data as Linha) : null
}

const MESES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro']
const br = (iso: string) => iso.split('-').reverse().join('/')
/** "Setembro de 2026" ou "Semana de 21/09/2026 a 27/09/2026" (o mesmo rótulo da função, com maiúscula). */
export function tituloResumo(r: Pick<ResumoSemanal, 'tipo' | 'inicio' | 'fim'>): string {
  if (r.tipo === 'mes') {
    const m = MESES[Number(r.inicio.slice(5, 7)) - 1]
    return `${m[0].toUpperCase()}${m.slice(1)} de ${r.inicio.slice(0, 4)}`
  }
  return `Semana de ${br(r.inicio)} a ${br(r.fim)}`
}

async function erroDe(error: Error): Promise<never> {
  const detalhe = error instanceof FunctionsHttpError ? await error.context.json().catch(() => null) : null
  throw new Error(detalhe?.erro ?? error.message)
}

/** Gera (ou gera de novo) o resumo de um período; sem `inicio`, a semana ou o mês passado. */
export async function gerarResumo(marcaId: string, tipo: TipoResumo, inicio?: string): Promise<{ motivoSemIa?: string }> {
  const { data, error } = await supabase.functions.invoke('resumo-semanal', { body: { marca_id: marcaId, periodo: tipo, inicio } })
  if (error) await erroDe(error)
  return data as { motivoSemIa?: string }
}

// ---------------------------------------------------------------------------
// E-mail: destinatários da marca, envio manual e o registro dos envios
// ---------------------------------------------------------------------------

export type ConfigRelatorio = { destinatarios: string[]; envioSemanal: boolean; envioMensal: boolean }
export const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/
/** "a@b.com, c@d.com" ou um por linha → lista sem repetição. */
export const lerEmails = (texto: string) => [...new Set(texto.split(/[\s,;]+/).map((e) => e.trim().toLowerCase()).filter(Boolean))]

export async function lerConfigRelatorio(marcaId: string): Promise<ConfigRelatorio> {
  const { data, error } = await supabase.from('relatorios_config').select('destinatarios, envio_semanal, envio_mensal').eq('marca_id', marcaId).maybeSingle()
  if (error) throw new Error(error.message)
  return { destinatarios: data?.destinatarios ?? [], envioSemanal: data?.envio_semanal ?? false, envioMensal: data?.envio_mensal ?? false }
}

export async function salvarConfigRelatorio(marcaId: string, c: ConfigRelatorio): Promise<void> {
  const { data, error } = await supabase
    .from('relatorios_config')
    .upsert({ marca_id: marcaId, destinatarios: c.destinatarios, envio_semanal: c.envioSemanal, envio_mensal: c.envioMensal, atualizado_em: new Date().toISOString() })
    .select('marca_id')
  if (error) throw new Error(/check constraint/i.test(error.message) ? 'Algum e-mail é inválido (ou passou de 20)' : error.message)
  // O RLS barra em silêncio: sem linha de volta, não salvou.
  if (!data?.length) throw new Error('Você não tem permissão para alterar os destinatários desta marca')
}

/** Manda o resumo por e-mail; sem `para`, aos destinatários cadastrados da marca. */
export async function enviarRelatorio(resumoId: string, para?: string[]): Promise<{ para: string[] }> {
  const { data, error } = await supabase.functions.invoke('enviar-relatorio', { body: { resumo_id: resumoId, para } })
  if (error) await erroDe(error)
  return data as { para: string[] }
}

export type EnvioRelatorio = { id: string; para: string[]; status: 'enviado' | 'erro'; mensagem: string | null; origem: 'manual' | 'agendada'; enviadoEm: string }

/** Os envios de um resumo (só quem gerencia marcas vê; para os outros, lista vazia). */
export async function listarEnvios(resumoId: string): Promise<EnvioRelatorio[]> {
  const { data, error } = await supabase
    .from('relatorios_envios')
    .select('id, para, status, mensagem, origem, enviado_em')
    .eq('resumo_id', resumoId)
    .order('enviado_em', { ascending: false })
    .limit(10)
  if (error) throw new Error(error.message)
  return (data ?? []).map((e) => ({ id: e.id, para: e.para, status: e.status, mensagem: e.mensagem, origem: e.origem, enviadoEm: e.enviado_em }))
}

export type AgendaResumo = { ativo: boolean; disponivel: boolean; quando: string; quando_mensal?: string }

export async function situacaoResumoSemanal(): Promise<AgendaResumo> {
  const { data, error } = await supabase.rpc('situacao_resumo_semanal')
  if (error) throw new Error(error.message)
  return data as AgendaResumo
}

export async function configurarResumoSemanal(ativo: boolean): Promise<AgendaResumo> {
  const { data, error } = await supabase.rpc('configurar_resumo_semanal', {
    p_ativo: ativo,
    p_url: ativo ? String(import.meta.env.VITE_SUPABASE_URL ?? '').replace(/\/+$/, '') : null,
    // O endereço deste site, para o link dos e-mails (só https; em localhost fica o que estava).
    p_site: ativo && window.location.protocol === 'https:' ? window.location.origin : null,
  })
  if (error) throw new Error(/permission denied/i.test(error.message) ? 'Você não tem permissão para agendar o resumo' : error.message)
  return data as AgendaResumo
}
